/**
 * AdminRutasA4.jsx · Agente A4 — Enrutamiento GPS diario.
 * Lista de técnicos + su ruta ordenada + mapa con polyline.
 */
import { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { MapContainer, TileLayer, Marker, Polyline, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Route, RefreshCw, Users, AlertTriangle, Clock, Loader2, MapPin, Truck,
} from "lucide-react";

const CENTER_EC = [-1.831239, -78.183406];
const numberIcon = (n, committed) => L.divIcon({
  html: `<div style="background:${committed ? "#DC2626" : "#0A3747"};color:#E9EE6E;width:26px;height:26px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:12px;box-shadow:0 2px 6px rgba(0,0,0,.35);border:2px solid white">${n}</div>`,
  className: "route-num-icon", iconSize: [26, 26], iconAnchor: [13, 13],
});

export default function AdminRutasA4() {
  const [dashboard, setDashboard] = useState(null);
  const [selectedTech, setSelectedTech] = useState(null);
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [computing, setComputing] = useState(false);
  const [delayFor, setDelayFor] = useState(null);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/a4/dashboard");
      setDashboard(data);
      if (data.routes_today?.length && !selectedTech) {
        setSelectedTech(data.routes_today[0].user_id);
      }
    } catch (e) { toast.error("Error cargando dashboard"); }
    finally { setLoading(false); }
  };

  const loadRoute = async (userId) => {
    if (!userId) return;
    try {
      const { data } = await api.get(`/a4/routes/${userId}`);
      setRoute(data.exists ? data : null);
    } catch (e) { toast.error("Error cargando ruta"); }
  };

  useEffect(() => { loadDashboard(); }, []);
  useEffect(() => { if (selectedTech) loadRoute(selectedTech); }, [selectedTech]);

  const computeRoute = async () => {
    if (!selectedTech) return;
    setComputing(true);
    try {
      await api.post(`/a4/routes/${selectedTech}/compute`);
      toast.success("Ruta recalculada");
      loadDashboard(); loadRoute(selectedTech);
    } catch (e) { toast.error("Error"); }
    finally { setComputing(false); }
  };

  const reportDelay = async (otId) => {
    const raw = window.prompt("Minutos de retraso:", "20");
    if (!raw) return;
    const delay_minutes = parseInt(raw, 10);
    if (!delay_minutes || delay_minutes < 1) return;
    setDelayFor(otId);
    try {
      const { data } = await api.post("/a4/delay-report", {
        user_id: selectedTech, odoo_task_id: otId, delay_minutes,
      });
      toast.success(
        data.triggered_client_notice
          ? `Retraso registrado · Mensaje enviado al próximo cliente · Ruta recalculada`
          : `Retraso registrado · Ruta recalculada`
      );
      loadRoute(selectedTech);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setDelayFor(null); }
  };

  const cancelStop = async (otId) => {
    if (!window.confirm("¿Cancelar esta parada y recalcular la ruta?")) return;
    try {
      await api.post("/a4/cancel-stop", { user_id: selectedTech, odoo_task_id: otId, reason: "Cancelada por agendador" });
      toast.success("Parada cancelada · Ruta recalculada");
      loadRoute(selectedTech);
    } catch (e) { toast.error("Error"); }
  };

  const plan = route?.plan || [];
  const polyline = plan.map(s => [s.lat, s.lon]);

  return (
    <Layout>
      <div className="space-y-4" data-testid="a4-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-2"
                 style={{ background: "#DBEAFE", border: "1px solid #60A5FA" }}>
              <Route size={11} className="text-blue-700" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-blue-700">Agente A4 · Enrutamiento GPS diario</span>
            </div>
            <h1 className="text-3xl font-bold" style={{ color: "#0A3747" }}>Rutas del día</h1>
            <p className="mt-1 text-sm text-gray-500">TSP · distancia + tráfico + ventanas comprometidas. Retrasos {'>'}15 min notifican al cliente automáticamente.</p>
          </div>
          <button onClick={computeRoute} disabled={computing || !selectedTech} className="neu-btn neu-btn-primary" data-testid="btn-recompute">
            {computing ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>} Recalcular ruta
          </button>
        </div>

        {/* KPIs */}
        {dashboard && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <KPI label="Rutas activas hoy" value={dashboard.routes_count || 0} icon={Truck} color="#0A3747"/>
            <KPI label="Retrasos reportados" value={dashboard.delays_today || 0} icon={AlertTriangle} color="#DC2626"/>
            <KPI label="Técnicos con OTs" value={(dashboard.routes_today || []).length} icon={Users} color="#059669"/>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Lista técnicos */}
          <div className="lg:col-span-1 space-y-2">
            {loading ? <div className="py-8 flex justify-center"><Loader2 className="animate-spin text-gray-400"/></div>
            : (dashboard?.routes_today || []).length === 0 ? (
              <div className="neu-card p-6 text-center text-xs text-gray-500">Sin técnicos con ruta hoy. Compute una primero.</div>
            )
            : (dashboard.routes_today || []).map(t => (
              <button key={t.user_id} onClick={() => setSelectedTech(t.user_id)}
                className={`w-full text-left neu-card p-3 hover:bg-gray-50 ${selectedTech === t.user_id ? "border-2 border-blue-500" : ""}`}
                data-testid={`tech-item-${t.user_id}`}>
                <div className="font-semibold text-sm" style={{ color: "#0A3747" }}>{t.user_name}</div>
                <div className="text-[10px] text-gray-500">{t.email}</div>
                <div className="mt-1 flex items-center gap-2 text-[11px] tabular-nums">
                  <span className="flex items-center gap-1"><MapPin size={10}/> {t.stops_count} paradas</span>
                  <span className="flex items-center gap-1"><Route size={10}/> {t.total_km} km</span>
                  <span className="flex items-center gap-1"><Clock size={10}/> {Math.round(t.total_minutes/60)}h</span>
                </div>
              </button>
            ))}
          </div>

          {/* Mapa + tabla */}
          <div className="lg:col-span-3 space-y-3">
            <div className="neu-card overflow-hidden" style={{ height: 380 }}>
              <MapContainer center={CENTER_EC} zoom={7} style={{ height: "100%", width: "100%" }} key={selectedTech || "empty"}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap" />
                {polyline.length > 1 && (
                  <Polyline positions={polyline} color="#0A3747" weight={3} opacity={0.8} dashArray="6, 4"/>
                )}
                {plan.map((s, i) => (
                  <Marker key={s.odoo_task_id} position={[s.lat, s.lon]} icon={numberIcon(i + 1, s.committed)}>
                    <Popup>
                      <div style={{ minWidth: 200 }}>
                        <div className="font-bold text-sm" style={{ color: "#0A3747" }}>#{i + 1} · OT {s.odoo_task_id}</div>
                        <div className="text-xs">{s.client_name}</div>
                        <div className="text-[10px] text-gray-500 mt-1">
                          ETA: {new Date(s.eta).toLocaleString("es-EC", { timeZone: "America/Guayaquil", dateStyle: "short", timeStyle: "short" })}<br/>
                          +{s.travel_km} km · +{s.travel_minutes} min
                          {s.committed && <div className="text-red-600 font-bold">🔒 Cita comprometida</div>}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>

            {route && plan.length > 0 && (
              <div className="neu-card overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-[9px] uppercase tracking-widest text-gray-500 border-b">
                    <tr>
                      <th className="px-2 py-2 text-center w-8">#</th>
                      <th className="px-2 py-2 text-left">OT · Cliente</th>
                      <th className="px-2 py-2 text-left w-32">ETA</th>
                      <th className="px-2 py-2 text-center w-24">Viaje</th>
                      <th className="px-2 py-2 text-center w-40">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.map((s, i) => (
                      <tr key={s.odoo_task_id} className={`border-b ${s.committed ? "bg-red-50" : ""}`} data-testid={`stop-${s.odoo_task_id}`}>
                        <td className="px-2 py-2 text-center font-bold" style={{ color: "#0A3747" }}>{i + 1}</td>
                        <td className="px-2 py-2">
                          <div className="font-semibold text-[#0A3747]">OT {s.task_number || s.odoo_task_id}{s.committed && <span className="ml-1 text-[9px] text-red-700 font-bold">🔒 CITA</span>}</div>
                          <div className="text-[10px] text-gray-500 truncate max-w-[240px]">{s.client_name}</div>
                        </td>
                        <td className="px-2 py-2 text-[10px] tabular-nums">
                          {new Date(s.eta).toLocaleString("es-EC", { timeZone: "America/Guayaquil", dateStyle: "short", timeStyle: "short" })}
                        </td>
                        <td className="px-2 py-2 text-center text-[10px] tabular-nums">
                          <div>{s.travel_km} km</div>
                          <div className="text-gray-500">+{s.travel_minutes} min</div>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <button onClick={() => reportDelay(s.odoo_task_id)} disabled={delayFor === s.odoo_task_id}
                            className="text-[10px] px-2 py-0.5 rounded border border-amber-400 text-amber-800 hover:bg-amber-50 mr-1"
                            data-testid={`delay-${s.odoo_task_id}`}>
                            {delayFor === s.odoo_task_id ? "..." : "Retraso"}
                          </button>
                          <button onClick={() => cancelStop(s.odoo_task_id)}
                            className="text-[10px] px-2 py-0.5 rounded border border-red-400 text-red-800 hover:bg-red-50"
                            data-testid={`cancel-${s.odoo_task_id}`}>
                            Cancelar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 text-xs">
                    <tr>
                      <td colSpan="2" className="px-2 py-2 font-semibold">Total del día</td>
                      <td colSpan="3" className="px-2 py-2 text-right tabular-nums font-bold">
                        {route.total_km} km · {Math.floor(route.total_minutes/60)}h {route.total_minutes%60}m · {route.stops_count} paradas
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function KPI({ label, value, icon: Icon, color }) {
  return (
    <div className="neu-card p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold" style={{ color }}>
        <Icon size={12}/> {label}
      </div>
      <div className="text-3xl font-bold mt-1 tabular-nums" style={{ color: "#0A3747" }}>{value}</div>
    </div>
  );
}
